import { computed, reactive, ref } from "vue";
import { $api } from "@/services/api";
import { useStore } from "vuex";
import { ApplicationStatusTypeIds, ProductIds } from "@/config/constants";
import { PelLoanConfigurationDetails } from "@/models/pel";
import type { School } from "@/models/school";
import type { EnrollmentTerm } from "@/models/school";
import type { InitialFundsRequest, LoanVM } from "@/models/opal";
import useFormatter from "@/use/formatter";
import useLoanConfigurationHelpers from "@/use/loanConfigurationModuleHelpers";
import useApplicationData from "@/composables/useApplicationData";
import useApplicationPatch from "@/composables/useApplicationPatch";
import { StudentVM } from "@/models/student";
import modalModuleActions from "@/use/modalModuleActions";
import type { FieldError } from "@/models";

export default function useApplicationInfo() {
  const NOT_YET_SELECTED = "Not Yet Selected";

  interface LoanDetails {
    academicTerms: string;
    graduationDate?: Date;
    graduationDateDisplay: string;
    requestedAmount?: number;
    requestedAmountDisplay: string;
    requestedAmountPerTerm?: number;
    requestedAmountPerTermDisplay: string;
    school?: School;
    schoolName: string;
  }

  const store = useStore();

  const {
    appData,
    borrowAmountModalAlertMessage,
    hasRepaymentBasedRates,
    hasResidency,
    isMedical,
    isDomesticGrad,
    isMedicalGrad,
    isParentLOC,
    isProfessionalGrad,
    isValidProfessionalGradFlow,
    productTypeId,
    programTypeId,
    useProfessionalGradLoanOptionsModal,
  } = useApplicationData();

  // Reactive
  let initialDrawRequest = reactive({} as InitialFundsRequest);

  // REFS

  const editingFromConfirmYourLoanDetails = ref<boolean>(false);
  const error = ref<boolean>(false);
  const dataRetrievalError = ref<boolean>(false);

  const fundsRequestBtnText = ref<string>("Request Funds");

  const gettingData = ref<boolean>(true);
  const info = ref({} as LoanDetails);
  const loanSvcError = ref<boolean>(false);
  const loanExists = ref<boolean | undefined>();
  const showLoanConfigurationModal = ref<boolean>(false);
  const showFundsRequestModal = ref<boolean>(false);
  const updatingApp = ref<boolean>(true);

  const { updateAppData } = useApplicationPatch(store, {
    updatingApp,
    error,
    editingFromConfirmYourLoanDetails,
  });

  // COMPUTED
  const confirmYourLoanDetailsModalActive = computed<boolean>(
    () => store.state.applicationInfoModule.confirmYourLoanDetailsModalActive,
  );

  const details = computed<PelLoanConfigurationDetails>(
    () => store.state.loanConfigurationModule.applicationInfo,
  );

  const applicationStatusId = computed<number>(
    () => store.state.appData?.application?.applicationStatusId,
  );

  const academicTerms = computed<string>(() => {
    if (isComplete.value) {
      return info.value?.academicTerms;
    } else {
      return details.value.academicTerms;
    }
  });

  const CONFIRMATION_OF_LOAN_TERMS_CONSENT_EXISTS = computed<boolean>(
    () => store.getters["consentsModule/CONFIRMATION_OF_LOAN_TERMS_CONSENT_EXISTS"],
  );

  const disbursements = computed(() => {
    const terms = enrollmentTerm.value.split("-") || [];
    const amount =
      isComplete.value && loan.value?.id > 0 && loan.value?.draws?.length > 0
        ? loan.value?.draws?.[loan.value?.draws?.length - 1]?.requestedAmount
        : details.value.loanAmount;

    return terms.map((term: string) => {
      return {
        term: term.trim(),
        amount: (amount || 0) / (terms || []).length,
      };
    });
  });

  const editable = computed<boolean>(
    () => store.state.loanConfigurationModule?.applicationInfo?.editable,
  );

  const editLoanTermsDisabled = computed<boolean>(() => details.value.loanTermsEditable === false);

  const enrollmentTerm = computed<string>(() => {
    const term =
      isComplete.value && loan.value?.id > 0 && loan.value?.draws?.length > 0
        ? loan.value?.draws?.[loan.value?.draws?.length - 1]?.enrollmentTerm?.description
        : details.value.academicTerms; //|| appData.value?.application?.schoolConfiguration?.academicTerm;
    return term || "Not Yet Selected";
  });

  const errors = computed<FieldError[]>(() => details.value?.errors || []);

  const hasSchoolError = computed<boolean>(
    () => errors.value?.find((e: FieldError) => e.fieldName === "SchoolName") !== undefined,
  );

  const initialDraw = computed(() => {
    return details.value?.initialDraw;
  });

  const isComplete = computed<boolean>(
    () => store.state.appData.application.applicationStatusId === ApplicationStatusTypeIds.COMPLETE,
  );

  const legalPacketGenerated = computed<boolean>(
    () => store.state.loanConfigurationModule?.applicationInfo?.legalPacketGenerated,
  );

  const loan = computed<LoanVM>(() => store.getters["fundsModule/Loan"]);

  const monthlyInSchoolPaymentLabel = computed<string>(() => {
    const defaultLabel = "";

    return details.value?.selectedRepaymentOption
      ? details.value?.selectedRepaymentOption?.monthlyInSchoolPaymentLabel || defaultLabel
      : details.value?.monthlyInSchoolPaymentLabel || defaultLabel;
  });

  const monthlyPostSchoolPaymentLabel = computed<string>(() => {
    const defaultLabel = "";

    return details.value?.selectedRepaymentOption
      ? details.value?.selectedRepaymentOption?.monthlyPostSchoolPaymentLabel || defaultLabel
      : details.value?.monthlyPostSchoolPaymentLabel || defaultLabel;
  });

  const requestedAmount = computed<string>(() => {
    return details.value.loanAmountDisplay;
  });

  const requestedAmountPerTerm = computed<string>(() => {
    return details.value.loanAmountPerTermDisplay;
  });

  const rateAndRepaymentStipsExist = computed<boolean>(() => {
    const keys = Object.keys(sortedApplicationStips.value);

    if (keys.includes("Rate and Repayment")) {
      return sortedApplicationStips.value["Rate and Repayment"]?.length > 0;
    } else {
      return false;
    }
  });

  const rateSelectionDisplay = computed<string>(() => {
    if (productTypeId.value === ProductIds.LOC) {
      return details.value?.selectedRatePlan && details.value?.selectedRatePlan?.rateTypeName
        ? `${details.value?.selectedRatePlan?.rateDisplay} ${details.value?.selectedRatePlan?.rateTypeName}`
        : "Not Yet Selected";
    }
    return details.value?.rateSelectionDisplay;
  });

  const repaymentTypeDisplay = computed<string>(() => {
    if (productTypeId.value === ProductIds.LOC) {
      return details.value?.selectedRatePlan
        ? `${details.value?.repaymentTypeName}`
        : "Not Yet Selected";
    }

    return details.value?.repaymentTypeDisplay;
  });

  const student = computed(() => details.value?.student);

  const savingChanges = computed<boolean>(() => store.state.loanConfigurationModule.savingChanges);

  const schoolName = computed<string>(() => {
    return details.value.schoolName || NOT_YET_SELECTED;
  });

  const schoolNameError = computed<FieldError | undefined>(() =>
    errors.value?.find((e: FieldError) => e.fieldName === "SchoolName"),
  );

  const showRequestFundsButton = computed<boolean>(() => isComplete.value === true);

  const { formatMoney } = useFormatter();

  const showBorrowAmountModal = computed<boolean>(
    () => store.state.applicationInfoModule.borrowAmountModalActive,
  );

  const showEstimatedMonthlyPayments = computed<boolean>(
    () => isMedical.value || isProfessionalGrad.value || isMedicalGrad.value,
  );

  const showLoanOptionsModal = computed<boolean>(
    () => store.state.applicationInfoModule.loanOptionsModalActive,
  );

  const showSchoolConfirmationModal = computed<boolean>(
    () => store.state.applicationInfoModule.schoolConfirmationModalActive,
  );

  const showStudentInformationModal = computed<boolean>(
    () => store.state.applicationInfoModule.studentInformationModalActive,
  );

  const sortedApplicationStips = computed(() => store.state.checklistModule.sortedApplicationStips);

  const { checkSchoolConfirmationModalChanges, setCacheItem } = useLoanConfigurationHelpers();

  function openBorrowAmountModal() {
    if (!editLoanTermsDisabled.value) {
      store.dispatch("applicationInfoModule/openModal", "BorrowAmountModal");
    } else {
      store.dispatch("modal/openModal", {
        title: "Alert",
        description: borrowAmountModalAlertMessage.value,
        allowBackgroundScroll: false,
        buttonsPosition: "right",
      });
    }
  }

  function openLoanConfigurationModal() {
    showLoanConfigurationModal.value = true;
    store.dispatch("applicationInfoModule/openModal", "LocLoanConfigurationModal");
  }

  function closeLoanConfigurationModal() {
    showLoanConfigurationModal.value = false;
    store.dispatch("applicationInfoModule/closeModal", "LocLoanConfigurationModal");
  }

  function closeBorrowAmountModal() {
    store.dispatch("applicationInfoModule/closeModal", "BorrowAmountModal");
  }

  function openStudentInformationModal() {
    store.dispatch("applicationInfoModule/openModal", "StudentInformationModal");
  }

  function openSchoolConfirmationModal() {
    store.dispatch("applicationInfoModule/openModal", "SchoolConfirmationModal");
  }

  function openLoanOptionsModal() {
    store.dispatch("applicationInfoModule/openModal", "LoanOptionsModal");
  }

  function closeLoanOptionsModal() {
    store.dispatch("applicationInfoModule/closeModal", "LoanOptionsModal");
  }

  function closeLoanConfigModal() {
    showLoanConfigurationModal.value = false;
    if (editingFromConfirmYourLoanDetails.value) {
      store.dispatch("applicationInfoModule/openModal", "ConfirmYourLoanDetails");
      editingFromConfirmYourLoanDetails.value = false;
    }
  }

  function closeSchoolConfirmationModal() {
    store.dispatch("applicationInfoModule/closeModal", "PelSchoolConfirmationModal");
    store.dispatch("applicationInfoModule/closeModal", "LocSchoolConfirmationModal");
  }

  function closeStudentInformationModal() {
    store.dispatch("applicationInfoModule/closeModal", "StudentInformationModal");
  }

  async function handleLoanConfigurationComplete(payload: any) {
    await complete(payload);
    showLoanConfigurationModal.value = false;
    store.dispatch("applicationInfoModule/openModal", "ConfirmYourLoanDetails");
  }

  async function handleStudentInformation(student: StudentVM) {
    store.dispatch("loanConfigurationModule/setSavingChanges", true);
    store.dispatch("setStudent", student);

    await getApplicationDetails();

    updatingApp.value = false;

    updatingApp.value = false;
    modalModuleActions.closeModal();
    closeStudentInformationModal();
  }

  async function complete(event: any) {
    updatingApp.value = true;

    if (event?.school || event?.selectedTerm || event?.hasSchoolConfigChanges === true) {
      await handleSchoolConfirmation(event);
    }

    if (
      appData.value?.application.schoolConfiguration?.schoolId &&
      typeof event === "object" &&
      Object.keys(event)?.includes("amount") &&
      event.amount >= 0 &&
      event.requestedAmountChanged
    ) {
      await handleBorrowAmount(event);
    }

    if (event?.selectedPaymentOption) {
      await handleLoanOption(event);
    }

    await getLatestInitialDrawInformation();

    if (editingFromConfirmYourLoanDetails.value) {
      store.dispatch("applicationInfoModule/openModal", "ConfirmYourLoanDetails");
      editingFromConfirmYourLoanDetails.value = false;
    } else {
      await getApplicationDetails();
    }

    updatingApp.value = false;
    store.dispatch("loanConfigurationModule/setSavingChanges", false);

    closeSchoolConfirmationModal();
    closeLoanOptionsModal();
    closeBorrowAmountModal();
    closeLoanConfigurationModal();
  }

  async function getLatestInitialDrawInformation() {
    info.value.requestedAmount = initialDrawRequest?.amount
      ? formatMoney(initialDrawRequest?.amount)
      : formatMoney(Number(0.0));

    const selectedRatePlanExists =
      appData.value &&
      appData.value?.application &&
      appData.value?.application?.ratePlans &&
      appData.value?.application?.ratePlans?.length > 0 &&
      appData.value?.application?.loanConfiguration?.applicationId;

    if (selectedRatePlanExists) {
      const selectedRatePlanId = appData.value?.application?.loanConfiguration?.ratePlanId;

      const selectedRatePlan = (appData.value?.application?.ratePlans || []).find(
        (l: any) => l.id === selectedRatePlanId,
      );

      store.dispatch("loanConfigurationModule/setSelectedRatePlan", selectedRatePlan);
    }
  }

  async function handleSchoolConfirmation(event: any) {
    console.log("handleSchoolConfirmation()", event);
    store.dispatch("loanConfigurationModule/setSavingChanges", true);

    const schoolConfigUpdated = event?.hasSchoolConfigChanges === true;

    if (legalPacketGenerated.value && schoolConfigUpdated) {
      setCacheItem("schoolConfigurationUpdated", schoolConfigUpdated);
    }

    if (schoolConfigUpdated) {
      await store.dispatch("setSchoolConfig", event);

      await store.dispatch(
        "loanConfigurationModule/setSchoolConfiguration",
        appData.value.application.schoolConfiguration,
      );

      if (store.state.loanConfigurationModule.applicationInfo.initialDraw !== null) {
        const initialFundsRequest = {
          ...store.state.loanConfigurationModule.applicationInfo.initialDraw,
          userId: store.state.appData?.currentApplicant?.userId,
          schoolDetailsId: event?.school?.schoolDetailsId,
          schoolDOEId: event?.school?.schoolDOEId,
          schoolBranchDOEId: event?.school?.schoolBranchDOEId,
          enrollmentTermId: event?.academicTermId,
        };

        // // Update initial draw
        const response = await $api.applications.addOrUpdateInitialDraw(
          appData.value?.application?.id,
          initialFundsRequest,
          editingFromConfirmYourLoanDetails.value,
        );

        if (response.applicationId) {
          error.value = false;
        } else {
          updatingApp.value = false;
          error.value = true;
        }
      }

      await updateAppData();

      if (productTypeId.value === ProductIds.PEL) {
        await getApplicationDetails();
      }
    }

    closeSchoolConfirmationModal();
    store.dispatch("loanConfigurationModule/setSavingChanges", false);
  }

  async function handleBorrowAmount(event: {
    amount: string | number;
    requestedAmountChanged: boolean;
  }) {
    const amount = Number(event.amount);

    if (
      appData.value?.application?.schoolConfiguration?.schoolId &&
      typeof event === "object" &&
      Object.keys(event)?.includes("amount") &&
      !isNaN(amount) &&
      Number(event.amount) >= 0
    ) {
      if ((store.state.schools || []).length <= 0) {
        const res = await $api.school.getSchoolsByProgram(
          store?.state?.appData?.application?.programTypeId,
          store?.state?.appData?.application?.productTypeId,
          store?.state?.appData?.application?.lenderId,
        );
        store.dispatch("setSchools", res.schools);
      }

      info.value.school = store?.state?.schools?.find(
        (s: School) => s.id === appData.value?.application?.schoolConfiguration?.schoolId,
      );

      info.value.requestedAmount = amount;

      const initialFundsRequest = {
        userId: store.state.appData?.currentApplicant?.userId,
        schoolDetailsId: info.value?.school?.schoolDetailsId,
        schoolDOEId: info.value?.school?.schoolDOEId,
        schoolBranchDOEId: info.value?.school?.schoolBranchDOEId,
        enrollmentTermId: appData.value?.application?.schoolConfiguration?.academicTermId,
        amount: amount || Number(0),
      };

      // Update initial draw
      const response = await $api.applications.addOrUpdateInitialDraw(
        appData.value?.application?.id,
        initialFundsRequest,
        editingFromConfirmYourLoanDetails.value,
      );

      // Happy path in CI, QA, UAT, & Prod
      if (!response.errors || response.applicationId) {
        initialDrawRequest = { ...response };
        store.commit("loanConfigurationModule/SET_APPLICATION_INFO_LOAN_AMOUNT", amount);

        return true;
      }
      // Error handling
      else {
        error.value = true;
        return false;
      }
    }
  }

  async function handleLoanOption(event?: {
    selectedPaymentOption: any;
    selectedRatePlanId: number;
    selectedByApplicantId: number;
  }) {
    if (event && event?.selectedPaymentOption) {
      // Update Loan Configuration
      const loanConfigUpdateRequest = {
        applicationId: appData.value?.application.id,
        selectedRatePlanId: event?.selectedRatePlanId,
        selectedByApplicantId: event?.selectedByApplicantId,
        repaymentTypeId: event?.selectedPaymentOption?.repaymentTypeId,
      };
      const response = await $api.applications.addOrUpdateAppLoanConfig(
        loanConfigUpdateRequest,
        editingFromConfirmYourLoanDetails.value,
      );

      // updates root store
      store.dispatch("updateLoanConfiguration", response);

      // Happy path in CI, QA, UAT, & Prod
      if (!response.errors || response.applicationId) {
        store.dispatch("loanConfigurationModule/setSelectedRateTypeId", response?.rateTypeId);

        store.dispatch("loanConfigurationModule/setSelectedRatePlanId", response?.ratePlanId);

        store.dispatch(
          "loanConfigurationModule/setSelectedRepaymentTypeId",
          response?.repaymentTypeId,
        );
      }

      // Error handling
      else {
        error.value = true;
      }

      // Check if stip for Rate/Repayment stips exist and get latest stip status
      if (rateAndRepaymentStipsExist.value) {
        await store.dispatch("checklistModule/getRateAndRepaymentStips");
      }
    }
  }

  async function getData() {
    if ((store.state.schools || []).length <= 0) {
      const res = await $api.school.getSchoolsByProgram(
        store?.state?.appData?.application?.programTypeId,
        store?.state?.appData?.application?.productTypeId,
        store?.state?.appData?.application?.lenderId,
      );
      store.dispatch("setSchools", res.schools);
    }

    await store.dispatch("getEnrollmentTerms");
  }

  async function getLastRequestedDraw(loan: any) {
    await getData();

    if (loan?.draws?.length === 0) {
      // Handle requested amount
      info.value.requestedAmount = formatMoney(Number(0.0)) || "Not Yet Selected";

      // Handle school
      const schoolId = store.state?.appData?.application?.schoolConfiguration?.schoolId;

      info.value.school = store.state.schools.find((s: School) => s.id === schoolId);

      info.value.schoolName = info.value.school?.schoolName
        ? info.value.school.schoolName
        : "Not Yet Selected";

      // Handle AGD
      const graduationDate = store.state?.appData?.application?.schoolConfiguration?.graduationDate;
      if (graduationDate && info.value.schoolName !== "Not Yet Selected") {
        const date = new Date(graduationDate);
        info.value.graduationDate = date;
        info.value.graduationDateDisplay = details.value.graduationDateDisplay;
      }

      // Handle enrollment term
      const enrollmentTerm = store.state.enrollmentTerms.find(
        (e: EnrollmentTerm) =>
          e.description === store.state?.appData?.application?.schoolConfiguration?.academicTerm,
      );

      if (enrollmentTerm) {
        info.value.academicTerms = enrollmentTerm?.description;
      }
    }

    if (loan?.draws?.length > 0) {
      const lastDraw = loan?.draws?.[loan?.draws?.length - 1];

      if (lastDraw?.requestedAmount >= 0) {
        info.value.requestedAmount = lastDraw?.requestedAmount;
        info.value.requestedAmountDisplay =
          formatMoney(lastDraw?.requestedAmount) || formatMoney(Number(0.0));

        info.value.requestedAmountPerTerm =
          lastDraw?.requestedAmount && lastDraw.enrollmentTerm?.description?.includes("-")
            ? (lastDraw?.requestedAmount || 0) / 2
            : lastDraw?.requestedAmount || 0;

        info.value.requestedAmountPerTermDisplay = formatMoney(info.value.requestedAmountPerTerm);
      }

      if (lastDraw?.requestedAmount > 0) {
        fundsRequestBtnText.value = "Request Additional Funds";
      }

      const schoolDOEId = lastDraw?.schoolCode;
      const schoolBranchDOEId = lastDraw?.schoolBranch;

      info.value.school = store.state.schools.find(
        (s: School) => s.schoolDOEId === schoolDOEId && s.schoolBranchDOEId === schoolBranchDOEId,
      );

      info.value.schoolName =
        info.value.school && info.value.school.schoolName
          ? info.value.school.schoolName
          : "Not Yet Selected";

      const gradDate = lastDraw?.anticipatedGraduationDate;
      if (gradDate && info.value.schoolName !== "Not Yet Selected") {
        const date = new Date(gradDate);
        const formatter = new Intl.DateTimeFormat("en-US", {
          month: "long",
          year: "numeric",
        });

        info.value.graduationDate = date;
        info.value.graduationDateDisplay = formatter.format(date);
      }

      const enrollmentTerm = lastDraw?.enrollmentTerm;
      if (enrollmentTerm) {
        info.value.academicTerms = enrollmentTerm.description;
      }

      const enrollmentStatus = lastDraw?.enrollmentStatus;
    }
  }

  async function getLoanDetails() {
    if (isComplete.value) {
      store.dispatch("applicationInfoModule/gettingLoanDetails", true);

      const res = await $api.applications.getLoanDetails(
        store?.state?.appData?.application?.referenceId,
        store?.state?.appData?.application?.lenderId,
      );

      if (res.loan) {
        const loan = res.loan;
        const availableToBorrow = res.availableToBorrowAmount;
        const canRequestFund = res.canRequestFund;
        const loanBoarded = res?.loanBoarded || res?.isLoanBoarded;

        const loanVM = {
          availableToBorrow: availableToBorrow,
          canRequestFund: canRequestFund,
          isLoanClosed: res.isLoanClosed,
          id: loan.id,
          draws: loan.draws,
          loanAmount: loan.loanAmount,
          loanBoarded: loanBoarded,
          fundsDisbursed: res.fundsDisbursed,
          minimum: undefined,
          referenceId: loan.referenceId,
          schoolLevelTypeId:
            store?.state?.appData?.application?.schoolConfiguration?.schoolLevelTypeId,
          certifications: res?.certificationModels || res?.certifications,
        } as LoanVM;

        store.commit("fundsModule/setCurrentLoan", loanVM);
        store.dispatch("loanConfigurationModule/setCertifications", loanVM.certifications);

        await getLastRequestedDraw(loan);

        loanExists.value = true;
        gettingData.value = false;
        updatingApp.value = false;
        return loan;
      } else if (res.error === 404) {
        loanExists.value = false;
        return false;
      } else if (res.error === 500 || res.error === true) {
        loanSvcError.value = true;
        updatingApp.value = false;
      }
    }

    store.dispatch("applicationInfoModule/gettingLoanDetails", false);
  }

  async function getApplicationDetails() {
    gettingData.value = true;
    updatingApp.value = false;
    dataRetrievalError.value = false;

    const response = await store.dispatch(
      "loanConfigurationModule/getPelDetails",
      store.state.appData?.application?.id,
    );

    if (!response) {
      dataRetrievalError.value = true;
    }

    initialDrawRequest = store.state.loanConfigurationModule?.applicationInfo?.initialDrawRequest;

    await getLoanDetails();

    gettingData.value = false;
  }

  function openSchoolConfirmationFromConfirm() {
    editingFromConfirmYourLoanDetails.value = true;
    store.dispatch("applicationInfoModule/closeModal", "ConfirmYourLoanDetails");
    openSchoolConfirmationModal();
  }

  function openLoanConfigurationFromConfirm() {
    editingFromConfirmYourLoanDetails.value = true;
    store.dispatch("applicationInfoModule/closeModal", "ConfirmYourLoanDetails");
    openLoanConfigurationModal();
  }
  function openLoanOptionsFromConfirm() {
    editingFromConfirmYourLoanDetails.value = true;
    store.dispatch("applicationInfoModule/closeModal", "ConfirmYourLoanDetails");
    openLoanOptionsModal();
  }

  function openBorrowAmountFromConfirm() {
    editingFromConfirmYourLoanDetails.value = true;
    store.dispatch("applicationInfoModule/closeModal", "ConfirmYourLoanDetails");
    openBorrowAmountModal();
  }

  function showPendingDisbursementNotice(loan: LoanVM) {
    if (loan.isLoanClosed === true) return false;

    return (
      (loan.availableToBorrow ?? 0) > 0 &&
      loan.canRequestFund === false &&
      (loan.draws?.length ?? 0) > 0
    );
  }

  return {
    appData,
    academicTerms,
    applicationStatusId,
    borrowAmountModalAlertMessage,
    closeBorrowAmountModal,
    closeLoanConfigModal,
    closeLoanConfigurationModal,
    closeLoanOptionsModal,
    closeSchoolConfirmationModal,
    closeStudentInformationModal,
    complete,
    confirmYourLoanDetailsModalActive,
    CONFIRMATION_OF_LOAN_TERMS_CONSENT_EXISTS,
    dataRetrievalError,
    details,
    disbursements,
    editable,
    editLoanTermsDisabled,
    editingFromConfirmYourLoanDetails,
    error,
    errors,
    formatMoney,
    fundsRequestBtnText,
    getApplicationDetails,
    getData,
    gettingData,
    getLoanDetails,
    handleBorrowAmount,
    handleLoanConfigurationComplete,
    handleSchoolConfirmation,
    handleStudentInformation,
    hasRepaymentBasedRates,
    hasResidency,
    hasSchoolError,
    initialDraw,
    initialDrawRequest,
    isComplete,
    isMedical,
    isDomesticGrad,
    isMedicalGrad,
    isParentLOC,
    isProfessionalGrad,
    isValidProfessionalGradFlow,
    legalPacketGenerated,
    loan,
    loanSvcError,
    monthlyInSchoolPaymentLabel,
    monthlyPostSchoolPaymentLabel,
    openBorrowAmountFromConfirm,
    openBorrowAmountModal,
    openLoanConfigurationModal,
    openLoanConfigurationFromConfirm,
    openLoanOptionsModal,
    openLoanOptionsFromConfirm,
    openSchoolConfirmationModal,
    openSchoolConfirmationFromConfirm,
    openStudentInformationModal,
    productTypeId,
    programTypeId,
    rateSelectionDisplay,
    repaymentTypeDisplay,
    requestedAmount,
    requestedAmountPerTerm,
    savingChanges,
    schoolName,
    schoolNameError,
    showBorrowAmountModal,
    showEstimatedMonthlyPayments,
    showFundsRequestModal,
    showLoanConfigurationModal,
    showLoanOptionsModal,
    showRequestFundsButton,
    showSchoolConfirmationModal,
    showStudentInformationModal,
    student,
    updateAppData,
    updatingApp,
    useProfessionalGradLoanOptionsModal,
    showPendingDisbursementNotice,
  };
}
